/**
 * ClientDiscoveryHandler.ts
 * 
 * BATON Handler for device/zone discovery on the Web Client.
 * 
 * This handler communicates with the Control Bridge's DiscoveryHandler
 * to get available audio zones (UPnP, Chromecast devices).
 * 
 * Commands (to server):
 *   - startScan      → Start zone discovery
 *   - stopScan       → Stop zone discovery
 *   - getZones       → Get all discovered zones
 *   - setActiveZone  → Select playback zone
 *   - getActiveZone  → Get current active zone
 *   - refreshZones   → Trigger immediate rescan
 * 
 * Events (from server):
 *   - zonesChanged       → Zone list updated
 *   - activeZoneChanged  → Active zone changed
 *   - scanComplete       → Scan finished
 * 
 * Usage:
 *   import { getClientDiscoveryHandler } from './ClientDiscoveryHandler';
 *   
 *   const handler = getClientDiscoveryHandler();
 *   handler.connect(webSocket);
 *   
 *   const zones = await handler.getZones();
 *   await handler.setActiveZone('upnp_living_room');
 */

import { useMaestroStore } from '../store/maestroStore';
import {
  isConnectorAvailable,
  getRenderers,
  onRenderersUpdated,
  type Renderer,
} from '../lib/connector';
import { isIOSNativeApp, detectPlatform, isWindowsConnectorHost, isDesktopMacOSUserAgent } from '../utils/platform';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { connectorRendererFromZone } from '../transport/gaplessSetNext';
import { overlayGaplessPrefOnZoneMetadata } from '../services/gaplessPlaybackSettings'; import { reconcileConnectorGapless } from '../services/gaplessConnectorReconcile';
import { syncRendererConnectivityFromZones } from '../transport/rendererConnectivity';
import { canRestoreSavedZone } from '../transport/playbackSession';
import { applyPinnedSavedZone, shouldAllowConnectorZoneRestoreRetry, wireConnectorDiscoveryBoot } from '../transport/zoneRestorePick';
import { remapPersistedConnectorZoneIfFolded } from '../transport/zoneFoldRemap';
import {
  deduplicateZones, hostsMayFold,
  mergeFoldedToMaps,
  resolveZoneIdViaAliases,
} from './zoneDeduplication'; import { setRendererIdAliases } from './liveRendererId';
import {
  classifyRendererZoneType,
  makeLocalComputerZone,
  renderersToZones,
  type Zone,
} from './rendererZoneMap';
import {
  isLocalZoneStorageId,
  LOCAL_ZONE_STORAGE_ID,
  userExplicitlyChoseSystemAudio,
} from '../utils/localZoneStorage';
import {
  logLocalOutputAutoDefault,
  logLocalOutputNoDac,
} from '../utils/localOutputLogging';
import { isConnectorBitPerfectCapable } from '../utils/connectorCapability';

console.log('[ClientDiscoveryHandler] MODULE LOADED — v2026-03-20-ios-native | isConnector:', isConnectorAvailable(), '| isIOSNative:', isIOSNativeApp());

// =============================================================================
// TYPES
// =============================================================================

export { classifyRendererZoneType } from './rendererZoneMap';
export type { Zone } from './rendererZoneMap';

export interface DiscoveryEvent {
  event: string;
  zones?: Zone[];
  activeZone?: Zone;
  timestamp?: number;
}

export interface DiscoveryResponse {
  success: boolean;
  zones?: Zone[];
  activeZone?: Zone;
  message?: string;
  error?: string;
}

type DiscoveryCommandPromise = {
  resolve: (value: DiscoveryResponse) => void;
  reject: (reason: Error) => void;
  timeout: NodeJS.Timeout;
};

// =============================================================================
// CLIENT DISCOVERY HANDLER
// =============================================================================

export class ClientDiscoveryHandler {
  readonly handlerId = 'discovery';
  readonly location = 'bridge';
  private static readonly CONNECTOR_ZONE_STORAGE_KEY = 'maestro_connector_active_zone';
  private static readonly ZONE_TYPE_PRIORITY: Record<string, number> = {
    browser: 100,
    upnp: 100,
    psaudio: 70,
    lua: 60,
    manual: 55,
    chromecast: 50,
    airplay: 40,
  };
  
  private ws: WebSocket | null = null;
  private pendingCommands: Map<string, DiscoveryCommandPromise> = new Map();
  private localZonesCache: Zone[] = [];
  private rendererIdAliases: Record<string, string> = {};
  private activeZone: Zone | null = null;
  private lastZonesSignature = '';
  private applyZonesTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingZones: Zone[] | null = null;
  
  // Tauri event unsubscribe handle (Connector mode only)
  private connectorUnlisten: (() => void) | null = null;
  
  // Callbacks for UI updates
  private onZonesChangedCallbacks: Set<(zones: Zone[]) => void> = new Set();
  private onActiveZoneChangedCallbacks: Set<(zone: Zone | null) => void> = new Set();
  
  private connectorInitialized = false;
  private iosNativeInitialized = false;
  private tauriPollTimer: ReturnType<typeof setInterval> | null = null;
  private rendererPollTimer: ReturnType<typeof setInterval> | null = null;
  private rendererPollCount = 0;

  private _isLocalZone(zone: Zone): boolean {
    return zone.type === 'browser'
      || zone.id === 'local_computer'
      || zone.id === 'this_computer'
      || zone.id === 'this_device';
  }

  /**
   * Sync both store.setActiveZone AND store.setActiveConnectorRenderer together.
   * TransportCommand reads activeConnectorRenderer — if we only set activeZone,
   * transport commands route to nothing.
   */
  private _updateStoreZone(zone: Zone | null) {
    const store = useMaestroStore.getState();
    const current = store.activeConnectorRenderer;
    const isRemote = zone && !this._isLocalZone(zone);

    if (isRemote) {
      if (current?.id === zone.id) return;
      store.setActiveZone(zone);
      store.setActiveConnectorRenderer(connectorRendererFromZone(zone));
      console.log('[ClientDiscoveryHandler] setActiveZone + setActiveConnectorRenderer:', zone.name, zone.id);
      if (isConnectorAvailable() && zone.type === 'local-native') {
        this.saveConnectorZoneId(zone.id);
      }
    } else if (zone) {
      store.setActiveZone(zone);
      store.setActiveConnectorRenderer(null);
      if (isConnectorAvailable()) {
        if (zone.type === 'local-native') {
          this.saveConnectorZoneId(zone.id);
        } else {
          this.saveConnectorZoneId(LOCAL_ZONE_STORAGE_ID);
        }
      }
    }
  }

  constructor() {
    console.log('[ClientDiscoveryHandler] Initialized');
    this.waitForNativeAndStart();
  }

  /**
   * Poll for __TAURI__ or __maestroNative availability and start discovery.
   * Both Tauri and the iOS native bridge inject asynchronously — may not exist
   * when the module first loads, so we poll every 200ms for up to 10s.
   */
  private waitForNativeAndStart(): void {
    if (isConnectorAvailable()) {
      this.startConnectorDiscovery();
      return;
    }
    if (isIOSNativeApp()) {
      this.startIOSNativeDiscovery();
      return;
    }
    let attempts = 0;
    this.tauriPollTimer = setInterval(() => {
      attempts++;
      if (isConnectorAvailable()) {
        if (this.tauriPollTimer) { clearInterval(this.tauriPollTimer); this.tauriPollTimer = null; }
        this.startConnectorDiscovery();
        return;
      }
      if (isIOSNativeApp()) {
        if (this.tauriPollTimer) { clearInterval(this.tauriPollTimer); this.tauriPollTimer = null; }
        this.startIOSNativeDiscovery();
        return;
      }
      if (attempts >= 50) {
        if (this.tauriPollTimer) { clearInterval(this.tauriPollTimer); this.tauriPollTimer = null; }
      }
    }, 200);
  }

  /**
   * Start Connector discovery — subscribe to renderer events and fetch zones.
   * Called once __TAURI__ is confirmed available.
   */
  private startConnectorDiscovery(): void {
    if (this.connectorInitialized) return;
    this.connectorInitialized = true; wireConnectorDiscoveryBoot(this);
    console.log('[ClientDiscoveryHandler] __TAURI__ ready — starting Connector discovery NOW');

    onRenderersUpdated((renderers: Renderer[]) => {
      console.log(`[ClientDiscoveryHandler] 🔄 Rust renderer update: ${renderers.length} devices`);
      const zones = renderersToZones(renderers);
      this.applyZones(zones);
      // The initial restoreConnectorZone runs before renderers arrive (0 results).
      // Re-attempt here so the saved zone is set once renderers are discovered.
      if (canRestoreSavedZone() && this.shouldRetryConnectorZoneRestore()) {
        this.restoreConnectorZone(zones).catch(() => {});
      }
    }).then((unlisten) => {
      this.connectorUnlisten = unlisten;
      console.log('[ClientDiscoveryHandler] ✅ Event subscription established');
    }).catch((err) => {
      console.warn('[ClientDiscoveryHandler] Failed to subscribe to renderer updates:', err);
    });

    this.getZones().then(response => {
      if (response.success && response.zones) {
        console.log(`[ClientDiscoveryHandler] ✅ Initial fetch: ${response.zones.length} zones`);
        this.applyZones(response.zones);
        this.startScan().then((scanResponse) => {
          if (scanResponse.success && scanResponse.zones) {
            console.log(`[ClientDiscoveryHandler] Scan complete: ${scanResponse.zones.length} zones`);
            this.applyZones(scanResponse.zones);
          }
          this.restoreConnectorZone(scanResponse.zones || response.zones || []).catch(() => {});
        }).catch(() => {
          this.restoreConnectorZone(response.zones || []).catch(() => {});
        });
      }
    }).catch(err => {
      console.error('[ClientDiscoveryHandler] Initial fetch failed:', err);
    });

    this.startRendererPolling();
  }

  // ---------------------------------------------------------------------------
  // iOS NATIVE DISCOVERY (parallel to Connector discovery)
  // ---------------------------------------------------------------------------

  /**
   * Start iOS native discovery — listen for CustomEvents from the Swift bridge
   * and request the initial renderer list. Called once __maestroNative is confirmed.
   */
  private startIOSNativeDiscovery(): void {
    if (this.iosNativeInitialized) return;
    this.iosNativeInitialized = true;
    console.log('[ClientDiscoveryHandler] 📲 __maestroNative ready — starting iOS native discovery');

    window.addEventListener('maestroRenderers', ((event: CustomEvent) => {
      const renderers: Renderer[] = event.detail;
      console.log(`[ClientDiscoveryHandler] 📲 iOS renderer update: ${renderers?.length ?? 0} devices`);
      if (Array.isArray(renderers)) {
        const zones = renderersToZones(renderers);
        this.applyZones(zones);
        if (this.shouldRetryIOSZoneRestore()) {
          this.restoreIOSNativeZone(zones).catch(() => {});
        }
      }
    }) as EventListener);

    window.addEventListener('maestroVolumeChanged', ((event: CustomEvent) => {
      const { deviceId, volume } = event.detail || {};
      if (deviceId !== undefined && volume !== undefined) {
        const { setVolume } = useMaestroStore.getState();
        setVolume(volume);
      }
    }) as EventListener);

    // Request initial renderer list and trigger a scan
    const native = (window as any).__maestroNative;
    if (native?.getRenderers) {
      native.getRenderers();
    }
    if (native?.startDiscovery) {
      native.startDiscovery();
    }

    // Poll for renderers to catch late-arriving devices
    this.startIOSRendererPolling();
  }

  /**
   * Poll for renderers on iOS every 3s for 30s. Native SSDP/RAAT discovery
   * runs in the background and devices appear incrementally.
   */
  private startIOSRendererPolling(): void {
    let pollCount = 0;
    const timer = setInterval(() => {
      // During playback, maestroRenderers → applyZones preserves the active zone row (see
      // preserveActiveZoneDuringPlayback). No transport calls from discovery.
      pollCount++;
      const native = (window as any).__maestroNative;
      if (native?.getRenderers) {
        native.getRenderers();
      }
      if (pollCount >= 10) {
        clearInterval(timer);
        console.log('[ClientDiscoveryHandler] 📲 iOS renderer polling complete (30s)');
      }
    }, 3000);
  }

  /**
   * Handle discovery commands locally when running inside the iOS native app.
   * The iPad IS the discovery source — no cloud round-trip needed.
   */
  private async handleIOSNativeCommand(
    cmd: string,
    payload: Record<string, unknown>,
  ): Promise<DiscoveryResponse> {
    switch (cmd) {
      case 'getZones':
      case 'startScan':
      case 'refreshZones': {
        const native = (window as any).__maestroNative;
        if (native?.getRenderers) {
          native.getRenderers();
        }
        // Return current cached zones (real data arrives via event)
        return { success: true, zones: this.localZonesCache.length > 0 ? this.localZonesCache : renderersToZones([]) };
      }

      case 'stopScan':
        return { success: true };

      case 'setActiveZone': {
        const zoneId = payload.zoneId as string;
        const zone = this.localZonesCache.find((z) => z.id === zoneId) || null;
        if (zone) {
          this.activeZone = zone;
          this.notifyActiveZoneChanged(zone);
          this.saveIOSNativeZoneId(zone.id);
          this._updateStoreZone(zone);
        }
        return { success: true, activeZone: zone || undefined };
      }

      case 'getActiveZone':
        return { success: true, activeZone: this.activeZone || undefined };

      default:
        return { success: false, error: `Unknown command: ${cmd}` };
    }
  }

  private static readonly IOS_ZONE_STORAGE_KEY = 'maestro_ios_active_zone';

  private getSavedIOSNativeZoneId(): string | null {
    try {
      return localStorage.getItem(ClientDiscoveryHandler.IOS_ZONE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private saveIOSNativeZoneId(zoneId: string): void {
    const normalized = isLocalZoneStorageId(zoneId) ? LOCAL_ZONE_STORAGE_ID : zoneId;
    try {
      localStorage.setItem(ClientDiscoveryHandler.IOS_ZONE_STORAGE_KEY, normalized);
    } catch { /* ignore */ }
  }

  private async restoreIOSNativeZone(zones: Zone[]): Promise<void> {
    if (!canRestoreSavedZone()) {
      console.log('[ClientDiscoveryHandler] Skipping iOS zone restore — playback session active');
      return;
    }
    const store = useMaestroStore.getState();
    if (store.activeConnectorRenderer) {
      const syncedId = store.activeConnectorRenderer.id;
      const match = zones.find((z) => z.id === syncedId);
      if (match) {
        console.log(`[ClientDiscoveryHandler] 📲 Adopting synced zone: ${match.name}`);
        this.activeZone = match;
        this.notifyActiveZoneChanged(match);
        return;
      }
    }

    const savedZoneId = this.getSavedIOSNativeZoneId();
    if (savedZoneId) {
      if (isLocalZoneStorageId(savedZoneId)) {
        const localZone = zones.find((z) => z.id === LOCAL_ZONE_STORAGE_ID) || makeLocalComputerZone();
        if (this.activeZone && this._isLocalZone(this.activeZone)) return;
        console.log('[ClientDiscoveryHandler] 📲 Restoring saved local output: System Audio');
        this.activeZone = localZone;
        this.notifyActiveZoneChanged(localZone);
        this._updateStoreZone(localZone);
        this.saveIOSNativeZoneId(LOCAL_ZONE_STORAGE_ID);
        return;
      }
      const saved = zones.find((z) => z.id === savedZoneId);
      if (saved) {
        console.log(`[ClientDiscoveryHandler] 📲 Restoring saved iOS zone: ${saved.name}`);
        this.activeZone = saved;
        this.notifyActiveZoneChanged(saved);
        this._updateStoreZone(saved);
        return;
      }
      return;
    }

    const localZone = zones.find((z) => z.id === LOCAL_ZONE_STORAGE_ID) || makeLocalComputerZone();
    if (!this.activeZone || !this._isLocalZone(this.activeZone)) {
      console.log('[ClientDiscoveryHandler] 📲 No saved zone — defaulting to System Audio');
      this.activeZone = localZone;
      this.notifyActiveZoneChanged(localZone);
      this._updateStoreZone(localZone);
      this.saveIOSNativeZoneId(LOCAL_ZONE_STORAGE_ID);
    }
  }

  /**
   * Poll getRenderers() every 3s for 30s. The initial IPC call often returns 0
   * renderers because Rust UPnP/SSDP discovery runs in the background and takes
   * several seconds. This polling catches those late arrivals even if the event
   * subscription failed due to WebKit's ipc:// mixed-content block.
   */
  private lastRendererIds: string = '';

  private startRendererPolling(): void {
    this.rendererPollCount = 0;
    if (this.rendererPollTimer) { clearInterval(this.rendererPollTimer); }
    this.rendererPollTimer = setInterval(async () => {
      this.rendererPollCount++;
      try {
        const renderers = await getRenderers();
        if (renderers.length > 0) {
          const idKey = renderers.map(r => r.id).sort().join(',');
          const zonesChanged = idKey !== this.lastRendererIds;
          if (zonesChanged) {
            this.lastRendererIds = idKey;
            console.log(`[ClientDiscoveryHandler] Poll #${this.rendererPollCount}: ${renderers.length} renderers found`);
            const zones = renderersToZones(renderers);
            this.applyZones(zones);
          }
          if (canRestoreSavedZone() && this.shouldRetryConnectorZoneRestore()) {
            const zones = zonesChanged ? this.localZonesCache : renderersToZones(renderers);
            this.restoreConnectorZone(zones).catch(() => {});
          }
        }
      } catch { /* IPC not ready yet */ }
      if (this.rendererPollCount >= 10) {
        if (this.rendererPollTimer) { clearInterval(this.rendererPollTimer); this.rendererPollTimer = null; }
        console.log('[ClientDiscoveryHandler] Renderer polling complete (30s)');
      }
    }, 3000);
  }
  
  // ---------------------------------------------------------------------------
  // CONNECTION MANAGEMENT
  // ---------------------------------------------------------------------------
  
  /**
   * Connect to WebSocket (shares connection with ClientTransportHandler)
   * On iOS native, discovery works via native bridge without WebSocket
   */
  connect(ws: WebSocket | null): void {
    this.ws = ws;
    
    if (ws) {
      console.log('[ClientDiscoveryHandler] Connected to WebSocket');
      console.log('[ClientDiscoveryHandler] WebSocket readyState:', ws.readyState);
    }
    
    const isConnector = isConnectorAvailable();
    const isIOS = isIOSNativeApp();

    // iOS native: start discovery if constructor missed it, otherwise refresh.
    if (isIOS) {
      if (!this.iosNativeInitialized) {
        console.log('[ClientDiscoveryHandler] 📲 iOS native on connect() — starting discovery');
        this.startIOSNativeDiscovery();
      } else {
        console.log('[ClientDiscoveryHandler] 📲 iOS native already initialized — refreshing');
        const native = (window as any).__maestroNative;
        if (native?.getRenderers) native.getRenderers();
      }
      return;
    }

    // Connector: start discovery if constructor missed it, otherwise refresh.
    if (isConnector) {
      if (!this.connectorInitialized) {
        console.log('[ClientDiscoveryHandler] Connector available on connect() — starting discovery now');
        this.startConnectorDiscovery();
      } else {
        console.log('[ClientDiscoveryHandler] Connector already initialized — refreshing zones');
        this.getZones().then(r => {
          if (r.success && r.zones) this.applyZones(r.zones);
        }).catch(() => {});
      }
      return;
    }

    // Non-Connector path: iOS native needs a longer delay for discovery.
    const delay = 1000;
    
    setTimeout(() => {
      console.log('[ClientDiscoveryHandler] Fetching initial zones...');
      this.getZones().then(response => {
        if (response.success && response.zones) {
          console.log(`[ClientDiscoveryHandler] ✅ Got ${response.zones.length} zones`);
          this.applyZones(response.zones);

          if (response.zones.length === 0) {
            console.log('[ClientDiscoveryHandler] No zones yet; starting scan…');
            this.startScan()
              .then(() => {
                setTimeout(() => {
                  this.getZones()
                    .then((r2) => {
                      if (r2.success && r2.zones) {
                        this.applyZones(r2.zones);
                        console.log(`[ClientDiscoveryHandler] Post-scan zones: ${r2.zones.length}`);
                      }
                    })
                    .catch(() => {});
                }, 2500);
              })
              .catch((err) => {
                console.warn('[ClientDiscoveryHandler] startScan failed:', err);
              });
          }

          if (isConnector) {
            this.startScan()
              .then((scanResponse) => {
                const zones = scanResponse.zones || response.zones || [];
                if (scanResponse.success && scanResponse.zones) {
                  console.log(`[ClientDiscoveryHandler] Connector scan complete — ${scanResponse.zones.length} zones`);
                  this.applyZones(scanResponse.zones);
                } else {
                  console.log(`[ClientDiscoveryHandler] startScan returned no zones, using initial ${zones.length}`);
                }
                return this.restoreConnectorZone(zones);
              })
              .catch((err) => {
                console.warn('[ClientDiscoveryHandler] Connector scan failed:', err);
                this.restoreConnectorZone(response.zones || []).catch(() => {});
              });
            return;
          }
        } else {
          console.error('[ClientDiscoveryHandler] getZones FULL:', JSON.stringify(response, null, 2));
        }
      }).catch(err => {
        console.error('[ClientDiscoveryHandler] Failed to fetch initial zones:', err);
      });
    }, delay);
  }
  
  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.ws = null;
    this.pendingCommands.forEach(promise => {
      clearTimeout(promise.timeout);
      promise.reject(new Error('Disconnected'));
    });
    this.pendingCommands.clear();

    // In Connector mode, keep the Tauri event subscription alive across
    // WebSocket reconnects — the Connector discovery is independent of WS.
    if (!isConnectorAvailable() && this.connectorUnlisten) {
      this.connectorUnlisten();
      this.connectorUnlisten = null;
    }

    console.log('[ClientDiscoveryHandler] Disconnected');
  }
  
  // ---------------------------------------------------------------------------
  // PUBLIC API - Commands
  // ---------------------------------------------------------------------------
  
  /**
   * Start zone discovery
   */
  async startScan(): Promise<DiscoveryResponse> {
    return this.sendCommand('startScan', {});
  }
  
  /**
   * Stop zone discovery
   */
  async stopScan(): Promise<DiscoveryResponse> {
    return this.sendCommand('stopScan', {});
  }
  
  /**
   * Get all discovered zones
   */
  async getZones(): Promise<DiscoveryResponse> {
    const response = await this.sendCommand('getZones', {});
    if (response.success && response.zones) {
      this.localZonesCache = response.zones;
      try {
        const { setDiscoveredZones } = useMaestroStore.getState();
        setDiscoveredZones(response.zones);
      } catch {
        // ignore
      }
    }
    return response;
  }

  /** Re-fetch renderer list from Connector IPC (covers missed startup emit). */
  async refreshConnectorRenderers(): Promise<void> {
    if (!isConnectorAvailable()) return;
    try {
      const renderers = await getRenderers();
      const zones = renderersToZones(renderers);
      console.log(`[ClientDiscoveryHandler] refreshConnectorRenderers: ${renderers.length} renderers → ${zones.length} zones`);
      this.applyZones(zones);
    } catch (err) {
      console.warn('[ClientDiscoveryHandler] refreshConnectorRenderers failed:', err);
    }
  }
  
  /**
   * Set the active playback zone
   */
  async setActiveZone(zoneId: string): Promise<DiscoveryResponse> {
    const preferredZone = this.getPreferredZone(zoneId, this.localZonesCache);
    const targetZoneId = preferredZone?.id || zoneId;
    const response = await this.sendCommand('setActiveZone', { zoneId: targetZoneId });
    if (response.success && response.activeZone) {
      const canonicalActiveZone = this.getPreferredZone(response.activeZone, this.localZonesCache) || response.activeZone;
      this.activeZone = canonicalActiveZone;
      this.notifyActiveZoneChanged(canonicalActiveZone);
      this.saveConnectorZoneId(canonicalActiveZone.id);
      
      this._updateStoreZone(canonicalActiveZone);

      const shouldUpgradeConnectorZone = isConnectorAvailable()
        && response.activeZone.type === 'airplay'
        && canonicalActiveZone.id !== response.activeZone.id;
      if (shouldUpgradeConnectorZone) {
        console.log(`[ClientDiscoveryHandler] Upgrading connector zone from ${response.activeZone.id} to ${canonicalActiveZone.id}`);
        return this.setActiveZone(canonicalActiveZone.id);
      }
    }
    return response;
  }
  
  /**
   * Get the current active zone
   */
  async getActiveZone(): Promise<DiscoveryResponse> {
    const response = await this.sendCommand('getActiveZone', {});
    if (response.success && response.activeZone) {
      this.activeZone = response.activeZone;
    }
    return response;
  }
  
  /**
   * Trigger immediate zone refresh
   */
  async refreshZones(): Promise<DiscoveryResponse> {
    const response = await this.sendCommand('refreshZones', {});
    if (response.success && response.zones) {
      this.localZonesCache = response.zones;
      this.notifyZonesChanged(response.zones);
      try {
        const { setDiscoveredZones } = useMaestroStore.getState();
        setDiscoveredZones(response.zones);
      } catch {
        // ignore
      }
    }
    return response;
  }
  
  /**
   * Get cached zones (synchronous)
   */
  getCachedZones(): Zone[] {
    return [...this.localZonesCache];
  }
  
  /**
   * Get cached active zone (synchronous)
   */
  getCachedActiveZone(): Zone | null {
    return this.activeZone;
  }
  
  // ---------------------------------------------------------------------------
  // EVENT CALLBACKS
  // ---------------------------------------------------------------------------
  
  /**
   * Subscribe to zones changed events
   */
  onZonesChanged(callback: (zones: Zone[]) => void): () => void {
    this.onZonesChangedCallbacks.add(callback);
    return () => this.onZonesChangedCallbacks.delete(callback);
  }
  
  /**
   * Subscribe to active zone changed events
   */
  onActiveZoneChanged(callback: (zone: Zone | null) => void): () => void {
    this.onActiveZoneChangedCallbacks.add(callback);
    return () => this.onActiveZoneChangedCallbacks.delete(callback);
  }
  
  // ---------------------------------------------------------------------------
  // EVENT HANDLING (from server)
  // ---------------------------------------------------------------------------
  
  /**
   * Handle incoming BATON events
   */
  handleEvent(event: DiscoveryEvent): void {
    const eventType = event.event;
    console.log(`[ClientDiscoveryHandler] Event received: ${eventType}`);
    
    switch (eventType) {
      case 'zonesChanged':
        if (event.zones) {
          this.applyZones(event.zones);
          this.maybeUpgradeActiveConnectorZone(event.zones);
        }
        break;
        
      case 'activeZoneChanged':
        if (event.activeZone) {
          this.activeZone = event.activeZone;
          this.notifyActiveZoneChanged(event.activeZone);
          this.saveConnectorZoneId(event.activeZone.id);
          this._updateStoreZone(event.activeZone);
        }
        break;
        
      case 'scanComplete':
        console.log('[ClientDiscoveryHandler] Scan complete');
        if (event.zones) {
          this.applyZones(event.zones);
          this.maybeUpgradeActiveConnectorZone(event.zones);
        }
        break;
        
      case 'volumeChanged':
        // Volume was changed by a network renderer command on the server
        // Update the local store to reflect the new volume
        console.log(`[ClientDiscoveryHandler] 🔊 Volume changed: ${(event as any).level}% via ${(event as any).method}`);
        if ((event as any).level !== undefined) {
          const { setVolume, setMuted } = useMaestroStore.getState();
          setVolume((event as any).level);
          if ((event as any).muted !== undefined) {
            setMuted((event as any).muted);
          }
        }
        break;
        
      case 'rendererStateChanged':
        // Renderer playback state changed (playing, paused, stopped)
        console.log(`[ClientDiscoveryHandler] 🎵 Renderer state: ${(event as any).state} on ${(event as any).zoneName}`);
        // Could update playback state in store here if needed
        break;
        
      default:
        console.warn(`[ClientDiscoveryHandler] Unknown event: ${eventType}`);
    }
  }
  
  /**
   * Handle BATON response
   */
  handleResponse(message: { id: string; payload: DiscoveryResponse }): void {
    const id = message.id;
    console.log(`[ClientDiscoveryHandler] 📥 Response received for id: ${id}`);
    console.log('[ClientDiscoveryHandler] Pending commands:', Array.from(this.pendingCommands.keys()));
    
    const pending = this.pendingCommands.get(id);
    
    if (pending) {
      console.log('[ClientDiscoveryHandler] ✅ Found pending command, resolving...');
      clearTimeout(pending.timeout);
      this.pendingCommands.delete(id);
      pending.resolve(message.payload);
    } else {
      console.warn('[ClientDiscoveryHandler] ⚠️ No pending command found for id:', id);
    }
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL - Connector-Local Command Handling (Tauri IPC)
  // ---------------------------------------------------------------------------

  /**
   * Handle discovery commands locally via Tauri IPC when running inside
   * the Connector. This is the primary path — no cloud round-trip.
   */
  private async handleConnectorCommand(
    cmd: string,
    payload: Record<string, unknown>,
  ): Promise<DiscoveryResponse> {
    switch (cmd) {
      case 'getZones':
      case 'startScan':
      case 'refreshZones': {
        try {
          const renderers = await getRenderers();
          const zones = renderersToZones(renderers);
          console.log(`[ClientDiscoveryHandler] Connector IPC: ${renderers.length} renderers → ${zones.length} zones`);
          return { success: true, zones };
        } catch (err) {
          console.error('[ClientDiscoveryHandler] Tauri getRenderers failed:', err);
          return { success: false, error: String(err) };
        }
      }

      case 'stopScan':
        return { success: true };

      case 'setActiveZone': {
        const zoneId = payload.zoneId as string;
        const zone = this.localZonesCache.find((z) => z.id === zoneId) || null;
        if (zone) {
          this.activeZone = zone;
          this.notifyActiveZoneChanged(zone);
          this.saveConnectorZoneId(zone.id);
          this._updateStoreZone(zone);
        }
        return { success: true, activeZone: zone || undefined };
      }

      case 'getActiveZone':
        return { success: true, activeZone: this.activeZone || undefined };

      default:
        console.warn(`[ClientDiscoveryHandler] Unknown Connector command: ${cmd}`);
        return { success: false, error: `Unknown command: ${cmd}` };
    }
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL - iOS Native Bridge Check
  // ---------------------------------------------------------------------------
  
  private getIOSBridge(): any {
    if (!detectPlatform().isIOSNativeApp) return null;
    return (window as any).__maestroNative
      || (window as any).webkit?.messageHandlers?.baton
      || (window as any).__MAESTRO_BRIDGE__;
  }
  
  private isNativeiOS(): boolean {
    return detectPlatform().isIOSNativeApp;
  }

  private getSavedConnectorZoneId(): string | null {
    if (!isConnectorAvailable()) return null;
    try {
      return localStorage.getItem(ClientDiscoveryHandler.CONNECTOR_ZONE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private saveConnectorZoneId(zoneId: string): void {
    if (!isConnectorAvailable() || !zoneId) return;
    const normalized = isLocalZoneStorageId(zoneId) ? LOCAL_ZONE_STORAGE_ID : zoneId;
    try {
      localStorage.setItem(ClientDiscoveryHandler.CONNECTOR_ZONE_STORAGE_KEY, normalized);
    } catch {
      // ignore
    }
  }

  /**
   * True when a renderer/zone is already selected (live) — in the store
   * (activeConnectorRenderer or discovery.activeZone) or in this handler's
   * own bookkeeping. Saved-zone restore must never override an existing
   * selection; it may only seed on a genuine cold start. Reads selection
   * state ONLY — never playbackState (discovery must not track transport).
   */
  private hasActiveRendererSelection(): boolean {
    // Handler bookkeeping reflects a confirmed IPC zone switch — store-only
    // boot shims (hydrateSavedZoneOnBoot) must not block cold-start restore.
    if (this.activeZone) return true;
    const store = useMaestroStore.getState();
    if (store.activeConnectorRenderer) return true;
    return false;
  }

  private shouldRetryConnectorZoneRestore(): boolean {
    const s = useMaestroStore.getState();
    return shouldAllowConnectorZoneRestoreRetry({ savedId: this.getSavedConnectorZoneId(), activeZone: this.activeZone || s.discovery?.activeZone, isLocalZone: (z) => this._isLocalZone(z), hasActiveRendererSelection: () => this.hasActiveRendererSelection() });
  }

  private shouldRetryIOSZoneRestore(): boolean {
    if (!canRestoreSavedZone()) return false;
    const saved = this.getSavedIOSNativeZoneId();

    if (!this.activeZone) return true;

    if (isLocalZoneStorageId(saved)) {
      return !this._isLocalZone(this.activeZone);
    }

    if (saved) {
      return this._isLocalZone(this.activeZone) && saved !== LOCAL_ZONE_STORAGE_ID;
    }

    return !this._isLocalZone(this.activeZone);
  }

  /**
   * Dedup zones discovered via multiple protocols at the SAME IP address.
   * UPnP ranks above psaudio/RAAT — matches Connector face folding.
   */
  private deduplicateAndTrackAliases(zones: Zone[]): Zone[] {
    const { zones: deduped, foldedTo } = deduplicateZones(zones);
    if (Object.keys(foldedTo).length > 0) {
      this.rendererIdAliases = mergeFoldedToMaps(this.rendererIdAliases, foldedTo); setRendererIdAliases(this.rendererIdAliases);
    }
    return deduped;
  }

  private applyFoldRemaps(merged: Zone[]): void {
    const remappedTo = remapPersistedConnectorZoneIfFolded(this.rendererIdAliases, merged);
    if (!remappedTo) return;

    const storeState = useMaestroStore.getState();
    const activeId = storeState.discovery?.activeZone?.id
      || storeState.activeConnectorRenderer?.id;
    if (activeId && resolveZoneIdViaAliases(activeId, this.rendererIdAliases) !== activeId) {
      const fresh = merged.find((z) => z.id === remappedTo);
      if (fresh) {
        this._updateStoreZone(fresh);
      }
    }
  }

  /**
   * While a network renderer is in use, discovery may briefly omit that row (dedupe/IP merge)
   * or native may reshuffle the list. Re-attach the prior row so the picker never drops the
   * active device. Uses renderer/selection only — not playbackState (discovery must not track transport).
   */
  private preserveActiveZoneDuringPlayback(deduped: Zone[]): Zone[] {
    const store = useMaestroStore.getState();
    const rid = store.activeConnectorRenderer?.id || '';
    const remoteRendererSelected =
      !!rid
      && rid !== 'local_computer'
      && rid !== 'this_computer'
      && rid !== 'this_device';
    const hasNetworkTarget = store.rendererHasStream || remoteRendererSelected;
    if (!hasNetworkTarget) return deduped;

    const activeId = store.activeConnectorRenderer?.id || store.discovery?.activeZone?.id;
    if (!activeId) return deduped;
    if (activeId === 'local_computer' || activeId === 'this_computer' || activeId === 'this_device') {
      return deduped;
    }

    if (deduped.some((z) => z.id === activeId)) return deduped;

    const prev = this.localZonesCache.find((z) => z.id === activeId);
    if (!prev) return deduped;

    console.log(
      '[ClientDiscoveryHandler] Playback active — preserving active zone row missing from latest discovery:',
      activeId,
    );
    return [...deduped, prev];
  }

  /**
   * applyZones is discovery-only: merge/dedupe the list and write `discoveredZones`.
   * Never call TransportCommand (pause/play/stop/playUri), never set playbackState,
   * never switch zones — those belong to user gestures / ZoneController / sync only.
   */
  private applyZones(zones: Zone[]): void {
    this.pendingZones = zones;
    if (this.applyZonesTimer) return;
    this.applyZonesTimer = setTimeout(() => {
      this.applyZonesTimer = null;
      const pending = this.pendingZones;
      this.pendingZones = null;
      if (pending) this.commitZones(pending);
    }, 150);
  }

  private zonesSignature(zones: Zone[]): string {
    return zones
      .map((z) => {
        const md = z.metadata || {};
        return [
          z.id,
          z.name,
          z.type,
          z.host || '',
          !!z.capabilities?.fixedVolume, !!z.capabilities?.volumeNotControllable,
          md.gapless_enabled === false ? 0 : 1,
          md.resync_delay_ms ?? 0,
          md.avt_supports_set_next === true ? 1 : 0,
          md.picker_selectable === false ? 0 : 1,
          md.picker_disabled_reason || '',
        ].join('|');
      })
      .join(';');
  }

  private commitZones(zones: Zone[]): void {
    const stateBefore = useMaestroStore.getState().playbackState;
    try {
      const deduped = reconcileConnectorGapless(this.deduplicateAndTrackAliases(zones)).map((z) => overlayGaplessPrefOnZoneMetadata(z));
      const merged = this.preserveActiveZoneDuringPlayback(deduped);
      this.applyFoldRemaps(merged);
      const signature = this.zonesSignature(merged);
      if (signature === this.lastZonesSignature) {
        return;
      }
      this.lastZonesSignature = signature;
      const beforeList = this.localZonesCache || [];
      const beforeIds = new Set(beforeList.map((z) => z.id));
      const afterIds = new Set(merged.map((z) => z.id));
      for (const z of merged) {
        if (!beforeIds.has(z.id)) {
          diagnosticLog('info', 'discovery', `Device found: ${z.name} (${z.type})`, {
            name: z.name,
            type: z.type,
            ip: z.host,
            protocol: z.type,
          });
        }
      }
      for (const z of beforeList) {
        if (!afterIds.has(z.id)) {
          diagnosticLog('warn', 'discovery', `Device lost: ${z.name}`, {
            name: z.name,
            type: z.type,
            ip: z.host,
            lastSeen: Date.now(),
          });
        }
      }
      console.log(`[ClientDiscoveryHandler] applyZones: ${zones.length} raw → ${deduped.length} after dedup → ${merged.length} merged`);
      diagnosticLog('info', 'discovery', `Renderer list updated: ${merged.length} zones`, { count: merged.length, names: merged.map(z => z.name || z.id) });
      this.localZonesCache = merged;
      this.notifyZonesChanged(merged);
      const storeState = useMaestroStore.getState();
      storeState.setDiscoveredZones(merged);
      syncRendererConnectivityFromZones();

      // Reconcile activeZone capabilities when the renderer list refreshes.
      // Capabilities like `fixedVolume` (AirLens) or updated volume capability
      // can arrive AFTER the user has already selected a zone — without this,
      // the MiniPlayer never learns the device has fixed volume and keeps
      // showing the slider. Also heals any partial-zone shim written by the
      // sync handler during a remote state adoption.
      const activeId = storeState.discovery?.activeZone?.id;
      if (activeId && activeId !== 'local_computer' && activeId !== 'this_computer' && activeId !== 'this_device') {
        const fresh = merged.find((z) => z.id === activeId);
        const current = storeState.discovery.activeZone;
        if (fresh && current) {
          const freshFixed = !!fresh.capabilities?.fixedVolume;
          const currentFixed = !!current.capabilities?.fixedVolume;
          const freshProtocols = JSON.stringify(fresh.capabilities?.protocols || []);
          const currentProtocols = JSON.stringify(current.capabilities?.protocols || []);
          if (freshFixed !== currentFixed || !!fresh.capabilities?.volumeNotControllable !== !!current.capabilities?.volumeNotControllable || freshProtocols !== currentProtocols) {
            console.log('[ClientDiscoveryHandler] Reconciling activeZone capabilities:', {
              id: activeId,
              fixedVolume: { was: currentFixed, now: freshFixed },
            });
            const protos = fresh.capabilities?.protocols || [];
            const summary = `${protos.join(',') || 'unknown'} fixedVol=${freshFixed}`;
            diagnosticLog('info', 'capability', `Capabilities for ${fresh.name || activeId}: ${summary}`, {
              name: fresh.name,
              protocols: protos,
              maxSampleRate: (fresh as any).max_sample_rate ?? (fresh as any).maxSampleRate,
              maxBitDepth: (fresh as any).max_bit_depth ?? (fresh as any).maxBitDepth,
            }, `${fresh.ip || ''}|${fresh.name || ''}|${fresh.manufacturer || ''}`);
            storeState.setActiveZone(fresh);
          }
        }
      }

      this.maybeAutoDefaultBitPerfectZone(merged).catch((err) => {
        console.warn('[ClientDiscoveryHandler] auto-default bit-perfect zone failed:', err);
      });
    } finally {
      // Never let zone updates change transport state
      if (useMaestroStore.getState().playbackState !== stateBefore) {
        useMaestroStore.setState({ playbackState: stateBefore });
      }
    }
  }

  private pickDefaultLocalNativeZone(zones: Zone[]): Zone | null {
    const natives = zones.filter((z) => z.type === 'local-native');
    if (natives.length === 0) return null;
    return natives[0];
  }

  private syncSelectedUsbDacFromZone(zone: Zone): void {
    const deviceId = (zone.metadata?.audio_device_id as string | undefined) || '';
    if (!deviceId) return;
    useMaestroStore.getState().setSelectedUsbDac({
      id: deviceId,
      name: zone.name,
    });
  }

  /**
   * Windows / Mac desktop: when a USB DAC appears and the user has not explicitly
   * chosen System Audio, make the bit-perfect local-native zone the active output.
   * Mac requires exactly one local-native zone; Windows keeps first-of-many behavior.
   */
  private async maybeAutoDefaultBitPerfectZone(zones: Zone[]): Promise<void> {
    if (!isConnectorAvailable()) return;
    if (!isConnectorBitPerfectCapable()) return;
    if (!canRestoreSavedZone()) return;

    const natives = zones.filter((z) => z.type === 'local-native');
    let native: Zone | null = null;
    if (isWindowsConnectorHost()) {
      native = this.pickDefaultLocalNativeZone(zones);
    } else if (detectPlatform().isTauriDesktop && isDesktopMacOSUserAgent()) {
      native = natives.length === 1 ? natives[0] : null;
    } else {
      return;
    }
    if (!native) return;

    const ps = useMaestroStore.getState().playbackState;
    if (ps === 'playing' || ps === 'loading') return;
    if (userExplicitlyChoseSystemAudio()) return;

    const active = this.activeZone || useMaestroStore.getState().discovery?.activeZone;
    if (active?.type === 'local-native' && active.id === native.id) return;

    const saved = this.getSavedConnectorZoneId();
    if (saved && !isLocalZoneStorageId(saved) && saved !== native.id) return;

    if (saved !== null && !this._isLocalZone(active)) return;

    logLocalOutputAutoDefault(native.name);
    await this.setActiveZone(native.id);
    this.syncSelectedUsbDacFromZone(native);
  }

  private getPreferredZone(zoneOrId: Zone | string | null | undefined, zones: Zone[]): Zone | null {
    if (!zoneOrId || !zones.length) return null;
    const selected = typeof zoneOrId === 'string'
      ? zones.find((zone) => zone.id === zoneOrId) || null
      : zoneOrId;

    if (!selected) return null;

    let best = selected;
    for (const candidate of zones) {
      const sameHost = hostsMayFold(selected.host, candidate.host);
      const sameName = selected.name?.toLowerCase() === candidate.name?.toLowerCase();
      const exactId = candidate.id === selected.id;
      if (!sameHost && !sameName && !exactId) continue;

      const candidatePriority = ClientDiscoveryHandler.ZONE_TYPE_PRIORITY[candidate.type] ?? 0;
      const bestPriority = ClientDiscoveryHandler.ZONE_TYPE_PRIORITY[best.type] ?? 0;
      if (candidatePriority > bestPriority) {
        best = candidate;
      }
    }

    return best;
  }

  private maybeUpgradeActiveConnectorZone(zones: Zone[]): void {
    const ps = useMaestroStore.getState().playbackState;
    if (ps === 'playing' || ps === 'loading') return;
    if (!isConnectorAvailable() || !this.activeZone) return;
    if (this.activeZone.type !== 'airplay') return;

    const preferredZone = this.getPreferredZone(this.activeZone, zones);
    if (!preferredZone || preferredZone.id === this.activeZone.id) return;

    console.log(`[ClientDiscoveryHandler] Auto-upgrading active connector zone to ${preferredZone.name} (${preferredZone.type})`);
    this.setActiveZone(preferredZone.id).catch((err) => {
      console.warn('[ClientDiscoveryHandler] Failed to auto-upgrade active zone:', err);
    });
  }

  private async restoreConnectorZone(zones: Zone[]): Promise<void> {
    if (isConnectorAvailable()) {
      const { ensureBootSessionHydratedFromDisk } = await import('../transport/bootSessionHydrate');
      await ensureBootSessionHydratedFromDisk();
    }
    if (!canRestoreSavedZone()) {
      console.log('[ClientDiscoveryHandler] Skipping saved zone restore — playback session active');
      return;
    }

    // Cold-start seed ONLY. Once a renderer is selected/live (e.g. the user's
    // local DAC), never override it with the persisted saved zone on a
    // reload/reconnect/connector-restart — the active zone changes only by
    // explicit user action, and the saved zone is never re-asserted as a
    // transport target for the next play().
    if (this.hasActiveRendererSelection()) {
      console.log('[ClientDiscoveryHandler] Active renderer already selected — not overriding with saved zone');
      return;
    }

    const savedZoneId = this.getSavedConnectorZoneId();
    if (savedZoneId) {
      if (isLocalZoneStorageId(savedZoneId)) {
        const localZone = zones.find((z) => z.id === LOCAL_ZONE_STORAGE_ID) || makeLocalComputerZone();
        if (this.activeZone && this._isLocalZone(this.activeZone)) return;
        console.log('[ClientDiscoveryHandler] Restoring saved local output: System Audio');
        useMaestroStore.getState().setSelectedUsbDac(null);
        await this.setActiveZone(localZone.id);
        return;
      }

      if (this.activeZone && this.activeZone.id === savedZoneId) return;
      const savedZone = this.getPreferredZone(savedZoneId, zones);
      if (savedZone) {
        if (this.activeZone && this.activeZone.id === savedZone.id) return;
        if (savedZone.type === 'local-native' && !isConnectorBitPerfectCapable()) {
          console.warn('[ClientDiscoveryHandler] Saved bit-perfect zone unavailable on this Connector — using System Audio');
          const localZone = zones.find((z) => z.id === LOCAL_ZONE_STORAGE_ID) || makeLocalComputerZone();
          await this.setActiveZone(localZone.id);
          return;
        }
        console.log(`[ClientDiscoveryHandler] Restoring saved connector zone: ${savedZone.name} (${savedZone.type})`);
        await this.setActiveZone(savedZone.id);
        if (savedZone.type === 'local-native') {
          this.syncSelectedUsbDacFromZone(savedZone);
        }
        return;
      }

      console.log(`[ClientDiscoveryHandler] Saved network zone not yet discovered: ${savedZoneId}`);
      applyPinnedSavedZone(savedZoneId);
      return;
    }

    if (isWindowsConnectorHost()) {
      if (!userExplicitlyChoseSystemAudio()) {
        const native = isConnectorBitPerfectCapable()
          ? this.pickDefaultLocalNativeZone(zones)
          : null;
        if (native) {
          logLocalOutputAutoDefault(native.name);
          await this.setActiveZone(native.id);
          this.syncSelectedUsbDacFromZone(native);
          return;
        }
      }
      logLocalOutputNoDac();
    }

    const localZone = zones.find((z) => z.id === LOCAL_ZONE_STORAGE_ID) || makeLocalComputerZone();
    if (this.activeZone && this._isLocalZone(this.activeZone)) return;
    console.log('[ClientDiscoveryHandler] No saved zone — defaulting to System Audio');
    await this.setActiveZone(localZone.id);
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL - Command Sending (WebSocket or Native Bridge)
  // ---------------------------------------------------------------------------
  
  private async sendCommand(cmd: string, payload: Record<string, unknown>): Promise<DiscoveryResponse> {
    // Connector-local path: use Tauri IPC directly.
    if (isConnectorAvailable()) {
      return this.handleConnectorCommand(cmd, payload);
    }

    // iOS native path: the iPad IS the discovery source.
    if (isIOSNativeApp()) {
      return this.handleIOSNativeCommand(cmd, payload);
    }

    const iosBridge = this.getIOSBridge();
    const hasWebSocket = this.ws && this.ws.readyState === WebSocket.OPEN;
    
    // On iOS native, use native bridge (discovery happens locally on device)
    // On web, use WebSocket to server
    if (!iosBridge && !hasWebSocket) {
      throw new Error('Not connected to server');
    }
    
    return new Promise((resolve, reject) => {
      const messageId = `discovery_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      
      // Set up timeout (longer for native discovery which can take time)
      const timeout = setTimeout(() => {
        this.pendingCommands.delete(messageId);
        console.warn(`[ClientDiscoveryHandler] Command ${cmd} timed out`);
        reject(new Error(`Command ${cmd} timed out`));
      }, 15000); // 15 seconds for discovery
      
      // Store pending promise
      this.pendingCommands.set(messageId, { resolve, reject, timeout });
      
      // Build BATON message envelope
      const message = {
        v: 1,
        id: messageId,
        ts: Date.now(),
        type: 'command',
        handler: 'discovery',
        location: 'local',
        payload: { cmd, ...payload },
      };
      
      const json = JSON.stringify(message);
      
      // ROUTING: iOS native bridge takes priority for discovery (local network)
      if (iosBridge) {
        console.log(`[ClientDiscoveryHandler] 📲 Sending to iOS native: ${cmd} (id: ${messageId})`);
        iosBridge.postMessage(json);
      } else if (hasWebSocket) {
        // Fallback to WebSocket (for local dev server or web-only scenarios)
        console.log(`[ClientDiscoveryHandler] 📤 Sent via WebSocket: ${cmd} (id: ${messageId})`);
        this.ws!.send(json);
      }
    });
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL - Notification
  // ---------------------------------------------------------------------------
  
  private notifyZonesChanged(zones: Zone[]): void {
    this.onZonesChangedCallbacks.forEach(cb => {
      try {
        cb(zones);
      } catch (e) {
        console.error('[ClientDiscoveryHandler] Callback error:', e);
      }
    });
  }

  private notifyActiveZoneChanged(zone: Zone | null): void {
    this.onActiveZoneChangedCallbacks.forEach(cb => {
      try {
        cb(zone);
      } catch (e) {
        console.error('[ClientDiscoveryHandler] Callback error:', e);
      }
    });
  }
  
  // ---------------------------------------------------------------------------
  // BATON HANDLER INTERFACE
  // ---------------------------------------------------------------------------
  
  canHandle(messageType: string): boolean {
    return messageType.startsWith('discovery:');
  }
  
  getInfo() {
    return {
      handlerId: this.handlerId,
      location: this.location,
      commands: [
        'discovery:startScan',
        'discovery:stopScan',
        'discovery:getZones',
        'discovery:setActiveZone',
        'discovery:getActiveZone',
        'discovery:refreshZones',
      ],
      events: [
        'discovery:zonesChanged',
        'discovery:activeZoneChanged',
        'discovery:scanComplete',
      ],
    };
  }
}

// =============================================================================
// SINGLETON EXPORT — created eagerly so __TAURI__ polling starts immediately
// =============================================================================

const clientDiscoveryHandlerInstance = new ClientDiscoveryHandler();

export function getClientDiscoveryHandler(): ClientDiscoveryHandler {
  return clientDiscoveryHandlerInstance;
}

